import { useState } from 'react';
import { Link } from 'react-router';
import { ColumnChart, type ColumnItem } from '../../../ui/charts';
import { Money } from '../../../ui';
import { copy } from '../copy';
import { CARD, ICON_BTN, LABEL, QUIET_LINK } from '../styles';

interface ColumnSectionProps {
  title: string;
  items: ColumnItem[];
  captionFor(key: string): string;
  drillFor(key: string): string;
  color?: string;
}

export function ColumnSection({ title, items, captionFor, drillFor, color }: ColumnSectionProps) {
  const [picked, setPicked] = useState<string | null>(null);
  const fallback =
    [...items].reverse().find((i) => i.valueMinor !== 0)?.key ??
    items[items.length - 1]?.key ??
    null;
  const key = picked !== null && items.some((i) => i.key === picked) ? picked : fallback;
  const idx = items.findIndex((i) => i.key === key);
  const item = items[idx];
  const prev = items[idx - 1];
  const next = items[idx + 1];

  return (
    <section aria-label={title} className={CARD}>
      <h2 className={`mb-3 ${LABEL}`}>{title}</h2>
      <ColumnChart
        items={items}
        ariaLabel={title}
        selectedKey={key}
        onSelect={setPicked}
        color={color}
      />
      {item ? (
        <div className="mt-3 flex flex-col gap-1">
          <p className="text-sm text-fg">
            {captionFor(item.key)} · <Money minor={item.valueMinor} />
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={copy.previousColumn}
              disabled={!prev}
              onClick={() => prev && setPicked(prev.key)}
              className={ICON_BTN}
            >
              ‹
            </button>
            <button
              type="button"
              aria-label={copy.nextColumn}
              disabled={!next}
              onClick={() => next && setPicked(next.key)}
              className={ICON_BTN}
            >
              ›
            </button>
            <Link to={drillFor(item.key)} className={`ml-auto ${QUIET_LINK}`}>
              {copy.seeEntries}
            </Link>
          </div>
        </div>
      ) : null}
    </section>
  );
}
